import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Briefcase, Calendar, Tag, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEAD_STATUSES = [
  { value: 'pending', label: 'Pending', color: 'yellow' },
  { value: 'assigned', label: 'Assigned', color: 'blue' },
  { value: 'contacted', label: 'Contacted', color: 'indigo' },
  { value: 'closed', label: 'Closed', color: 'green' },
  { value: 'bad_lead', label: 'Bad Lead', color: 'red' }
];

const LeadManagement = ({ leads, setShowCreateLeadDialog, fetchData }) => {
  const [updatingLeadId, setUpdatingLeadId] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleStatusUpdate = async (leadId, newStatus) => {
    setUpdatingLeadId(leadId);
    try {
      await axios.post(
        `${API}/admin/leads/${leadId}/status`,
        {},
        {
          params: { status: newStatus },
          withCredentials: true
        }
      );
      toast.success('Lead status updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lead status');
    } finally {
      setUpdatingLeadId(null);
    }
  };

  const getStatusColor = (status) => {
    const statusObj = LEAD_STATUSES.find(s => s.value === status);
    return statusObj?.color || 'gray';
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedLeads(leads.map(lead => lead.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (leadId) => {
    if (selectedLeads.includes(leadId)) {
      setSelectedLeads(selectedLeads.filter(id => id !== leadId));
    } else {
      setSelectedLeads([...selectedLeads, leadId]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) return;
    
    setIsDeleting(true);
    try {
      const response = await axios.post(
        `${API}/admin/leads/bulk-delete`,
        { ids: selectedLeads },
        { withCredentials: true }
      );
      
      toast.success(`Successfully deleted ${response.data.deleted_count} lead(s)`);
      setSelectedLeads([]);
      setBulkDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete leads');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold">All Leads</h3>
            {selectedLeads.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setBulkDeleteDialogOpen(true)}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Selected ({selectedLeads.length})
              </Button>
            )}
          </div>
          <button
            onClick={() => setShowCreateLeadDialog(true)}
            className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 flex items-center gap-2"
          >
            <Briefcase className="w-4 h-4" />
            Create New Lead
          </button>
        </div>

        {leads.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No leads yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedLeads.length === leads.length && leads.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead Info</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Property</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type & Timeline</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned To</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leads.map(lead => (
                  <tr key={lead.id} className={selectedLeads.includes(lead.id) ? 'bg-orange-50' : ''}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedLeads.includes(lead.id)}
                        onChange={() => handleSelectLead(lead.id)}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{lead.lead_name}</p>
                        <p className="text-xs text-gray-500">{lead.lead_email}</p>
                        <p className="text-xs text-gray-500">{lead.lead_phone}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div>
                        <p className="font-medium">{lead.property_address}</p>
                        <p className="text-xs">{lead.property_city}, TX {lead.property_zip}</p>
                        {lead.estimated_value && (
                          <p className="text-xs text-gray-500">Est: ${lead.estimated_value.toLocaleString()}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div className="space-y-1">
                        {lead.lead_type && (
                          <div className="flex items-center gap-1">
                            <Tag className="w-3 h-3" />
                            <span className={`text-xs font-semibold ${
                              lead.lead_type === 'buyer' ? 'text-blue-600' : 'text-green-600'
                            }`}>
                              {lead.lead_type === 'buyer' ? 'Buyer' : 'Seller'}
                            </span>
                          </div>
                        )}
                        {lead.timeline && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span className="text-xs text-gray-600">{lead.timeline}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        value={lead.status}
                        onValueChange={(newStatus) => handleStatusUpdate(lead.id, newStatus)}
                        disabled={updatingLeadId === lead.id}
                      >
                        <SelectTrigger className={`w-32 border-${getStatusColor(lead.status)}-300 bg-${getStatusColor(lead.status)}-50`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LEAD_STATUSES.map(status => (
                            <SelectItem key={status.value} value={status.value}>
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {lead.assigned_agents && lead.assigned_agents.length > 0 ? (
                        <div>
                          {lead.assigned_agents.map((agent, idx) => (
                            <div key={idx} className="mb-1">
                              <p className="font-medium text-xs">{agent.realtor_name}</p>
                              <p className="text-xs text-gray-500">{agent.realtor_email}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">Not assigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Delete {selectedLeads.length} Lead(s)
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                You are about to permanently delete <strong>{selectedLeads.length}</strong> lead(s).
                This action cannot be undone.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : `Delete ${selectedLeads.length} Lead(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadManagement;
